import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from '../utils/clsx';

// Reuse DS primitives where available
import { Avatar as DSAvatar } from '../components/Avatar/Avatar';
import DSButton from '../components/Button/Button';
import DSCheckbox from '../components/Checkbox/Checkbox';
import DSDrawer from '../components/Drawer/Drawer';
import DSInput from '../components/Input/Input';
import DSMenu, { MenuItem as DSMenuItem } from '../components/Menu/Menu';
import DSModal from '../components/Modal/Modal';
import DSPopover from '../components/Popover/Popover';
import DSRadio from '../components/Radio/Radio';
import DSSelect from '../components/Select/Select';
import DSSkeleton from '../components/Skeleton/Skeleton';
import { StardustSparksOnlyIcon as DSSpinner } from '../components/Spinner/Spinners';
import DSSwitch from '../components/Switch/Switch';
import DSTabs from '../components/Tabs/Tabs';
import DSTextarea from '../components/Textarea/Textarea';
import TextareaAutosizeDS from '../components/TextareaAutosize/TextareaAutosize';
import DSTooltip from '../components/Tooltip/Tooltip';

const GRID_BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
};

const DESC_BREAKPOINT_KEYS = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'];
const ASC_BREAKPOINT_KEYS = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

const defaultViewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

const getBreakpointForWidth = (width) => {
  for (const key of DESC_BREAKPOINT_KEYS) {
    if (width >= GRID_BREAKPOINTS[key]) {
      return key;
    }
  }
  return 'xs';
};

const resolveResponsiveValue = (value, width) => {
  if (value == null) return value;
  if (typeof value !== 'object' || Array.isArray(value)) return value;

  for (const key of DESC_BREAKPOINT_KEYS) {
    if (width >= GRID_BREAKPOINTS[key] && value[key] !== undefined) {
      return value[key];
    }
  }

  for (const key of ASC_BREAKPOINT_KEYS) {
    if (value[key] !== undefined) {
      return value[key];
    }
  }

  return undefined;
};

const resolveBreakpointConfig = (config, width) => {
  if (!config) return undefined;

  for (const key of DESC_BREAKPOINT_KEYS) {
    if (width >= GRID_BREAKPOINTS[key] && config[key] != null) {
      return config[key];
    }
  }

  for (const key of ASC_BREAKPOINT_KEYS) {
    if (config[key] != null) {
      return config[key];
    }
  }

  return undefined;
};

const GridContext = React.createContext({
  horizontalGutter: 0,
  verticalGutter: 0,
  wrap: true,
  width: defaultViewportWidth,
  breakpoint: getBreakpointForWidth(defaultViewportWidth),
});

// Button
export const Button = ({ children, variant, type, shape, icon, leftIcon, rightIcon, ...rest }) => (
  <DSButton variant={variant} shape={shape || 'default'} leftIcon={leftIcon || icon} rightIcon={rightIcon} {...rest}>{children}</DSButton>
);

// Input and TextArea
export const Input = Object.assign(
  React.forwardRef((props, ref) => <DSInput ref={ref} {...props} />),
  {
    TextArea: (props) => <DSTextarea {...props} />,
    Autosize: (props) => <TextareaAutosizeDS {...props} />,
    Password: React.forwardRef((props, ref) => <DSInput ref={ref} type="password" {...props} />),
    Group: ({ compact, children, className, style }) => (
      <div className={className} style={{ display: 'flex', gap: compact ? 0 : 8, ...style }}>
        {children}
      </div>
    ),
  }
);

// Modal (maps open/onClose)
export const Modal = ({
  open,
  visible,
  onCancel,
  onClose,
  onOk,
  title,
  footer,
  okText,
  cancelText,
  okButtonProps,
  cancelButtonProps,
  okType,
  confirmLoading,
  className,
  children,
  ...rest
}) => (
  <DSModal
    open={open ?? visible}
    onClose={onClose || onCancel}
    onCancel={onCancel}
    onOk={onOk}
    title={title}
    className={className}
    footer={footer}
    okText={okText}
    cancelText={cancelText}
    okButtonProps={okButtonProps}
    cancelButtonProps={cancelButtonProps}
    okType={okType}
    confirmLoading={confirmLoading}
    {...rest}
  >
    {children}
  </DSModal>
);

Modal.confirm = (...args) => DSModal.confirm?.(...args);
Modal.destroyAll = () => DSModal.destroyAll?.();

// Drawer (maps placement -> side)
export const Drawer = ({ open, visible, onClose, placement='right', width, children, className }) => (
  <DSDrawer open={open ?? visible} onClose={onClose} side={placement} className={className}>
    <div style={width ? { width } : undefined}>{children}</div>
  </DSDrawer>
);

// Popover (forward props to DS Popover)
export const Popover = ({
  content,
  children,
  open,
  onOpenChange,
  placement,
  overlayClassName,
  contentClassName,
  contentStyle,
  surfaceStyle,
  trigger,
  arrow,
  title,
  getPopupContainer,
  zIndex,
  defaultOpen,
  destroyOnHidden,
  mouseEnterDelay,
  mouseLeaveDelay,
  autoAdjustOverflow,
  color,
  closeOnBlur,
  closeOnContentClick,
}) => (
  <DSPopover
    className={overlayClassName}
    title={title}
    content={typeof content === 'function' ? content() : content}
    contentClassName={contentClassName}
    contentStyle={contentStyle}
    surfaceStyle={surfaceStyle}
    placement={placement}
    trigger={trigger}
    arrow={arrow}
    open={open}
    onOpenChange={onOpenChange}
    getPopupContainer={getPopupContainer}
    zIndex={zIndex}
    defaultOpen={defaultOpen}
    destroyOnHidden={destroyOnHidden}
    mouseEnterDelay={mouseEnterDelay}
    mouseLeaveDelay={mouseLeaveDelay}
    autoAdjustOverflow={autoAdjustOverflow}
    color={color}
    closeOnBlur={closeOnBlur}
    closeOnContentClick={closeOnContentClick}
  >
    {children}
  </DSPopover>
);

// Tooltip (accept title or content)
export const Tooltip = ({ title, content, children, className }) => (
  <DSTooltip content={content ?? title} className={className}>{children}</DSTooltip>
);

// Tabs & Tab (very small abstraction)
export const Tabs = ({ value, onChange, children, defaultValue }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  const first = items[0] && items[0].props?.value;
  const [active, setActive] = useState(value ?? defaultValue ?? first ?? '0');
  useEffect(() => { if (value !== undefined) setActive(value); }, [value]);
  const dsTabs = items.map((c) => ({ id: String(c.props.value), label: c.props.icon || c.props.label || c.props.children, content: null }));
  return (
    <div>
      <DSTabs tabs={dsTabs} defaultTab={String(active)} />
      <div>
        {items.map((c) => (
          <div key={String(c.props.value)} hidden={String(active) !== String(c.props.value)}>{c.props.children}</div>
        ))}
      </div>
    </div>
  );
};
export const Tab = ({ children }) => <>{children}</>;

// Select (accept simple value/onChange and children Option)
export const Select = ({ value, onChange, children, options, ...rest }) => {
  const opts = options ?? React.Children.map(children, (c) => ({ value: c.props.value, label: c.props.children })) ?? [];
  return (
    <DSSelect value={value} onChange={onChange} options={opts} {...rest} />
  );
};
Select.Option = ({ value, children }) => <option value={value}>{children}</option>;
export const Menu = DSMenu;
export const MenuItem = DSMenuItem;
Menu.Item = MenuItem;

export const List = ({ children, className }) => <div className={className}>{children}</div>;
export const ListItem = ({ children, className, onClick, button: _button, ...rest }) => (
  <DSMenuItem className={className} onClick={onClick} {...rest}>{children}</DSMenuItem>
);
export const ListItemText = ({ children }) => <span>{children}</span>;
export const Collapse = ({ in: open, children }) => open ? <div>{children}</div> : null;

const alignMap = {
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end',
  stretch: 'stretch',
};

const justifyMap = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  'space-around': 'space-around',
  'space-between': 'space-between',
  'space-evenly': 'space-evenly',
};

const parseFlex = (flexValue) => {
  if (flexValue == null) return undefined;
  if (typeof flexValue === 'number') {
    return `${flexValue} ${flexValue} auto`;
  }

  if (typeof flexValue === 'string') {
    if (/^\d+(\.\d+)?(px|em|rem|%)$/.test(flexValue)) {
      return `0 0 ${flexValue}`;
    }
    return flexValue;
  }

  return undefined;
};

export const Row = ({
  children,
  className,
  style,
  gutter = 0,
  align = 'top',
  justify = 'start',
  wrap = true,
  ...rest
}) => {
  const [viewportWidth, setViewportWidth] = useState(defaultViewportWidth);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const breakpoint = useMemo(() => getBreakpointForWidth(viewportWidth), [viewportWidth]);

  const [horizontalGutterProp, verticalGutterProp] = useMemo(() => {
    if (Array.isArray(gutter)) {
      return [gutter[0], gutter[1]];
    }
    return [gutter, 0];
  }, [gutter]);

  const horizontalGutter = resolveResponsiveValue(horizontalGutterProp ?? 0, viewportWidth) ?? 0;
  const verticalGutter = resolveResponsiveValue(verticalGutterProp ?? 0, viewportWidth) ?? 0;
  const resolvedAlign = resolveResponsiveValue(align, viewportWidth) ?? 'top';
  const resolvedJustify = resolveResponsiveValue(justify, viewportWidth) ?? 'start';
  const shouldWrap = wrap !== false;

  const baseStyle = {
    display: 'flex',
    flexWrap: shouldWrap ? 'wrap' : 'nowrap',
    alignItems: alignMap[resolvedAlign] ?? alignMap.top,
    justifyContent: justifyMap[resolvedJustify] ?? justifyMap.start,
    marginLeft: horizontalGutter ? -(horizontalGutter / 2) : undefined,
    marginRight: horizontalGutter ? -(horizontalGutter / 2) : undefined,
    marginTop: verticalGutter ? -(verticalGutter / 2) : undefined,
    marginBottom: verticalGutter ? -(verticalGutter / 2) : undefined,
  };

  return (
    <GridContext.Provider
      value={{
        horizontalGutter,
        verticalGutter,
        wrap: shouldWrap,
        width: viewportWidth,
        breakpoint,
      }}
    >
      <div
        className={className}
        style={{ ...baseStyle, ...style }}
        {...rest}
      >
        {children}
      </div>
    </GridContext.Provider>
  );
};

export const Col = ({
  children,
  span,
  style,
  className,
  offset,
  order,
  pull,
  push,
  flex,
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
  ...rest
}) => {
  const { horizontalGutter, verticalGutter, width } = useContext(GridContext);

  const responsiveConfig = useMemo(
    () => ({ xs, sm, md, lg, xl, xxl }),
    [xs, sm, md, lg, xl, xxl]
  );

  const viewportWidth = width ?? defaultViewportWidth;
  const breakpointConfig = useMemo(
    () => resolveBreakpointConfig(responsiveConfig, viewportWidth),
    [responsiveConfig, viewportWidth]
  );

  let mergedProps = {
    span,
    offset,
    order,
    pull,
    push,
    flex,
  };

  if (typeof breakpointConfig === 'number') {
    mergedProps = { ...mergedProps, span: breakpointConfig };
  } else if (breakpointConfig && typeof breakpointConfig === 'object') {
    mergedProps = { ...mergedProps, ...breakpointConfig };
  }

  const {
    span: mergedSpan,
    offset: mergedOffset,
    order: mergedOrder,
    pull: mergedPull,
    push: mergedPush,
    flex: mergedFlex,
  } = mergedProps;

  const colStyle = {
    boxSizing: 'border-box',
    paddingLeft: horizontalGutter ? horizontalGutter / 2 : undefined,
    paddingRight: horizontalGutter ? horizontalGutter / 2 : undefined,
    paddingTop: verticalGutter ? verticalGutter / 2 : undefined,
    paddingBottom: verticalGutter ? verticalGutter / 2 : undefined,
  };

  if (mergedOrder != null) {
    colStyle.order = mergedOrder;
  }

  if (mergedOffset) {
    colStyle.marginLeft = `${(mergedOffset / 24) * 100}%`;
  }

  if (mergedPull || mergedPush) {
    colStyle.position = 'relative';
  }

  if (mergedPull) {
    colStyle.right = `${(mergedPull / 24) * 100}%`;
  }

  if (mergedPush) {
    colStyle.left = `${(mergedPush / 24) * 100}%`;
  }

  if (mergedFlex != null) {
    colStyle.flex = parseFlex(mergedFlex);
  }

  if (mergedSpan != null) {
    if (Number(mergedSpan) === 0) {
      colStyle.display = 'none';
      colStyle.flex = 'none';
      colStyle.maxWidth = 0;
      colStyle.width = 0;
    } else {
      const widthPercent = (Number(mergedSpan) / 24) * 100;
      if (mergedFlex == null) {
        colStyle.flex = `0 0 ${widthPercent}%`;
      }
      colStyle.maxWidth = `${widthPercent}%`;
      colStyle.width = `${widthPercent}%`;
    }
  }

  return (
    <div
      className={className}
      style={{ ...colStyle, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
};

// ButtonGroup (very lightweight wrapper)
export const ButtonGroup = ({ children, className, style, variant }) => (
  <div className={className} style={{ display: 'inline-flex', gap: 0, ...style }} data-variant={variant}>
    {children}
  </div>
);

// Space
export const Space = ({ children, size='8px', direction='horizontal' }) => (
  <div style={{ display: 'flex', flexDirection: direction === 'vertical' ? 'column' : 'row', gap: size }}>{children}</div>
);

// Tag
export const Tag = ({ children, color, style, className }) => (
  <span className={className} style={{ display: 'inline-block', background: 'var(--surface-2)', borderRadius: 4, padding: '2px 8px', fontSize: 12, ...style }}>{children}</span>
);

// Card
export const Card = ({ children, className, style }) => (
  <div className={className} style={{ background: 'var(--card-bg)', border: '1px solid var(--surface-3)', borderRadius: 8, padding: 16, ...style }}>{children}</div>
);

// Typography (subset)
export const Typography = ({ children, style, className }) => <div className={className} style={style}>{children}</div>;
Typography.Text = ({ children, style, className }) => <span className={className} style={style}>{children}</span>;
Typography.Title = ({ level=3, children, style, className }) => {
  const Tag = `h${Math.min(6, Math.max(1, level))}`;
  return <Tag className={className} style={style}>{children}</Tag>;
};

// Badge (AntD-like props)
export const Badge = ({
  color,
  count,
  classNames,
  dot = false,
  offset,
  overflowCount = 99,
  showZero = false,
  size = 'default',
  status,
  styles,
  text,
  title,
  children,
  style,
  className,
}) => {
  const rootStyle = { position: children ? 'relative' : 'inline-block', display: 'inline-block', ...style, ...(styles?.root || {}) };
  const badgeColor = color || (status === 'success' ? 'var(--success)' : status === 'processing' ? 'var(--brand)' : status === 'error' ? 'var(--error)' : status === 'warning' ? 'var(--warning)' : undefined);

  // Status dot mode (no child overlay)
  if (status) {
    return (
      <span className={className} style={rootStyle} title={title}>
        <span
          className={classNames?.indicator}
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: 999,
            background: badgeColor || 'var(--text-muted)',
            marginRight: 6,
            ...(styles?.indicator || {}),
          }}
          aria-hidden
        />
        {text ? <span className={classNames?.text} style={styles?.text}>{text}</span> : null}
      </span>
    );
  }

  // Overlay mode
  const hasCount = typeof count === 'number' ? (showZero ? true : count > 0) : !!count;
  const showBadge = dot || hasCount;
  const displayCount = typeof count === 'number' && count > overflowCount ? `${overflowCount}+` : count;
  const sz = size === 'small' ? 16 : 20;
  const dx = Array.isArray(offset) ? Number(offset[0] || 0) : 10;
  const dy = Array.isArray(offset) ? Number(offset[1] || 0) : -10;
  const indicatorStyle = {
    position: children ? 'absolute' : 'relative',
    top: children ? 0 : 0,
    right: children ? 0 : 0,
    transform: (children || Array.isArray(offset)) ? `translate(${dx}px, ${dy}px)` : undefined,
    background: badgeColor || (dot ? 'crimson' : 'var(--brand)'),
    color: '#fff',
    borderRadius: 999,
    fontSize: 12,
    lineHeight: `${sz - 4}px`,
    height: dot ? 8 : sz,
    minWidth: dot ? 8 : sz,
    padding: dot ? 0 : '0 6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 0 1px var(--surface)',
    ...(styles?.indicator || {}),
  };
  return (
    <span className={className} style={rootStyle}>
      {children}
      {showBadge ? (
        <span className={classNames?.indicator} style={indicatorStyle} title={title} aria-hidden={dot && !title}>
          {dot ? '' : displayCount}
        </span>
      ) : null}
    </span>
  );
};

// Badge.Ribbon
Badge.Ribbon = ({ color, placement='end', text, children, className, style }) => (
  <div className={className} style={{ position: 'relative', display: 'inline-block', ...style }}>
    <div
      style={{
        position: 'absolute',
        top: 8,
        [placement === 'start' ? 'left' : 'right']: -6,
        transform: placement === 'start' ? 'rotate(-45deg)' : 'rotate(45deg)',
        background: color || 'var(--brand)',
        color: '#fff',
        padding: '2px 8px',
        fontSize: 12,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {text}
    </div>
    {children}
  </div>
);

// Layout primitives
export const Box = ({ children, className, style, component:Comp='div', ...rest }) => React.createElement(Comp, { className, style, ...rest }, children);

// Pagination (very simple)
export const Pagination = ({ current=1, pageSize=10, total=0, onChange }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <Button onClick={() => onChange && onChange(Math.max(1, current - 1), pageSize)} disabled={current <= 1}>Prev</Button>
      <span>{current} / {pages}</span>
      <Button onClick={() => onChange && onChange(Math.min(pages, current + 1), pageSize)} disabled={current >= pages}>Next</Button>
    </div>
  );
};

// Table (columns + dataSource)
export const Table = ({ columns=[], dataSource=[], rowKey='id', pagination=false, className, style }) => (
  <table className={className} style={{ width: '100%', borderCollapse: 'collapse', ...style }}>
    <thead>
      <tr>
        {columns.map((c) => (
          <th key={String(c.key || c.dataIndex || c.title)} style={{ textAlign: 'left', borderBottom: '1px solid var(--surface-3)', padding: 8 }}>{c.title}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {dataSource.map((row,i) => (
        <tr key={String(typeof rowKey === 'function' ? rowKey(row) : row[rowKey] || i)}>
          {columns.map((c) => (
            <td key={String(c.key || c.dataIndex || c.title)} style={{ borderBottom: '1px solid var(--surface-2)', padding: 8 }}>
              {c.render ? c.render(row[c.dataIndex], row, i) : row[c.dataIndex]}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

export const TableContainer = ({ children, component:Comp='div', ...rest }) => <Comp {...rest}>{children}</Comp>;
export const TableHead = ({ children }) => <thead>{children}</thead>;
export const TableBody = ({ children }) => <tbody>{children}</tbody>;
export const TableRow = ({ children, ...rest }) => <tr {...rest}>{children}</tr>;
export const TableCell = ({ children, header, ...rest }) => header ? <th {...rest}>{children}</th> : <td {...rest}>{children}</td>;
export const Paper = ({ children, className, style, component:Comp='div' }) => <Comp className={className} style={style}>{children}</Comp>;

// Slider
export const Slider = ({ min=0, max=100, value, onChange, step=1, className, style, range, ...rest }) => {
  const isRange = range || Array.isArray(value);
  if (isRange) {
    const [v0, v1] = Array.isArray(value) ? value : [min, max];
    const low = Math.min(v0 ?? min, v1 ?? max);
    const high = Math.max(v0 ?? min, v1 ?? max);
    const handleLow = (e) => {
      const nextLow = Number(e.target.value);
      const next = [Math.min(nextLow, high), high];
      onChange && onChange(next);
    };
    const handleHigh = (e) => {
      const nextHigh = Number(e.target.value);
      const next = [low, Math.max(nextHigh, low)];
      onChange && onChange(next);
    };
    return (
      <div className={className} style={{ display: 'grid', gap: 8, ...style }}>
        {/* <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={low}
          onChange={handleLow}
          {...rest}
        /> */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={high}
          onChange={handleHigh}
          {...rest}
        />
      </div>
    );
  }
  return (
    <input
      type="range"
      className={className}
      style={style}
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange && onChange(Number(e.target.value))}
      {...rest}
    />
  );
};

export const Divider = ({ className, style }) => <hr className={className} style={style} />;

// Image
export const Image = ({ src, alt, className, style, preview, ...rest }) => (
  <img src={src} alt={alt} className={className} style={style} {...rest} />
);

// Carousel (very simple)
export const Carousel = ({ children, className, style /* arrows, autoplay ignored */ }) => (
  <div className={className} style={{ display: 'flex', overflowX: 'auto', gap: 12, ...style }}>
    {React.Children.toArray(children).map((c, i) => (
      <div key={i} style={{ flex: '0 0 auto' }}>{c}</div>
    ))}
  </div>
);

export const Switch = DSSwitch;

export const Checkbox = Object.assign(
  React.forwardRef((props, ref) => <DSCheckbox ref={ref} {...props} />),
  {
    Group: ({ value = [], onChange, children, className }) => {
      const selected = new Set(value);
      const toggle = (val) => {
        const next = new Set(selected);
        if (next.has(val)) next.delete(val); else next.add(val);
        onChange && onChange(Array.from(next));
      };
      return (
        <div className={className} role="group">
          {React.Children.map(children, (child) => {
            if (!child) return null;
            const val = child.props?.value ?? child.props?.children;
            const label = child.props?.children;
            const checked = selected.has(val);
            return (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(val)} />
                <span>{label}</span>
              </label>
            );
          })}
        </div>
      );
    },
  }
);

// Radio + Group (single-select)
export const Radio = Object.assign(
  (props) => <DSRadio {...props} />,
  {
    Group: ({ value, onChange, children, className, name }) => (
      <div className={className} role="radiogroup">
        {React.Children.map(children, (child, i) => {
          if (!child) return null;
          const val = child.props?.value ?? i;
          const label = child.props?.children ?? child.props?.label;
          const checked = String(value) === String(val);
          return (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginRight: 12 }}>
              <input
                type="radio"
                name={name}
                checked={checked}
                onChange={() => onChange && onChange({ target: { value: val } })}
              />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
    )
  }
);

// Avatar
// Avatar + Group (basic group with max count)
const AvatarGroup = ({ children, max, size, shape='circle' }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  const limit = (max && (max.count || max.maxCount)) || items.length;
  const shown = items.slice(0, limit);
  const extra = items.length - shown.length;
  const sizeMap = { xxs: 24, xs: 32, small: 32, sm: 36, default: 36, md: 48, large: 64, lg: 64, xl: 96 };
  const sizePx = typeof size === 'number' ? size : (sizeMap[String(size || 'default')] || 36);
  const extraStyle = {
    background: 'var(--surface-2)',
    width: sizePx,
    height: sizePx,
    borderRadius: 999,
    fontSize: Math.max(10, Math.round(sizePx * 0.35)),
    border: '1px solid var(--surface-3)',
    ...max?.style,
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {shown.map((c, i) => {
        const child = React.isValidElement(c) ? React.cloneElement(c, {
          size: c.props.size ?? size,
          shape: c.props.shape ?? shape,
          style: { ...(c.props.style || {}), zIndex: (shown.length - i) },
        }) : c;
        return (
          <span key={i} style={{ marginLeft: i === 0 ? 0 : -8 }}>{child}</span>
        );
      })}
      {extra > 0 && (
        <span style={{ marginLeft: -8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...extraStyle }}>+{extra}</span>
        </span>
      )}
    </div>
  );
};

export const Avatar = Object.assign(
  (props) => <DSAvatar {...props} />,
  { Group: AvatarGroup }
);

// Skeleton, Spinner, Flex
export const Skeleton = ({ width, height }) => <DSSkeleton width={width} height={height} />;
Skeleton.Node = ({ style, block }) => <div style={{ background: 'var(--surface-3)', borderRadius: 6, ...style }} />;
Skeleton.Input = ({ style, block }) => <div style={{ background: 'var(--surface-3)', height: 32, borderRadius: 6, ...style }} />;
export const Flex = ({ gap='8px', vertical=false, children }) => (
  <div style={{ display: 'flex', flexDirection: vertical ? 'column' : 'row', gap }}>{children}</div>
);
export const TextareaAutosize = (props) => <TextareaAutosizeDS {...props} />;

// Result (very simple)
export const Result = ({ status, title, subTitle, extra }) => (
  <div style={{ padding: 16 }}>
    <div style={{ fontSize: 20, marginBottom: 8 }}>{title}</div>
    {subTitle && <div style={{ color: 'var(--muted)', marginBottom: 12 }}>{subTitle}</div>}
    {extra}
  </div>
);

// Spin
export const Spin = ({ spinning, children }) => (
  <div style={{ position: 'relative' }}>
    {spinning && (
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.05)' }}>
        <DSSpinner />
      </div>
    )}
    {children}
  </div>
);
export const Spinner = DSSpinner;

// Upload (basic input type=file)
export const Upload = ({ children, onChange, multiple, accept, disabled }) => {
  const inputRef = useRef(null);

  const openPicker = (event) => {
    event?.preventDefault?.();
    if (disabled) return;
    inputRef.current?.click?.();
  };

  const child = React.isValidElement(children)
    ? React.cloneElement(children, {
        onClick: (event) => {
          children.props?.onClick?.(event);
          if (!event?.defaultPrevented) {
            openPicker(event);
          }
        },
      })
    : <Button onClick={openPicker} disabled={disabled}>Upload</Button>;

  return (
    <span style={{ display: 'inline-flex', cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {child}
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          onChange && onChange({ fileList: Array.from(e.target.files || []) });
          if (e?.target) e.target.value = '';
        }}
      />
    </span>
  );
};

// Date/Time pickers using native inputs
export const DatePicker = ({ value, onChange, disableFuture, disablePast, ...rest }) => (
  <input type="date" value={value || ''} onChange={(e) => onChange && onChange(e.target.value)} {...rest} />
);
export const DateTimePicker = ({ value, onChange, disableFuture, disablePast, ...rest }) => (
  <input type="datetime-local" value={value || ''} onChange={(e) => onChange && onChange(e.target.value)} {...rest} />
);
export const TimePicker = ({ value, onChange, ...rest }) => (
  <input type="time" value={value || ''} onChange={(e) => onChange && onChange(e.target.value)} {...rest} />
);

// Simple TextField (alias of Input to help previous usage)
export const TextField = React.forwardRef((props, ref) => <DSInput ref={ref} {...props} />);
export const Autocomplete = ({ options = [], value, onChange, renderInput, ...rest }) => {
  const [input, setInput] = useState('');
  return (
    <div>
      {renderInput ? renderInput({
        inputProps: { value: input, onChange: (e) => setInput(e.target.value) }
      }) : <input value={input} onChange={(e) => setInput(e.target.value)} />}
      {/* very lightweight: emit selection on blur to first match */}
      <div style={{ display:'none' }}>{options.length}</div>
    </div>
  );
};

// Icons are still provided via design-system/icons elsewhere

// Minor stubs to keep previous API shapes compiling
export const InputAdornment = ({ children }) => <>{children}</>;
export const IconButton = ({ children, ...rest }) => <button type="button" {...rest}>{children}</button>;

// message/notification stubs
export const message = {
  success: (t) => console.log('success:', t),
  info: (t) => console.log('info:', t),
  warning: (t) => console.warn('warning:', t),
  error: (t) => console.error('error:', t),
  loading: (t) => {
    console.log('loading:', t);
    return () => {};
  },
};
export const notification = {
  open: ({ message: m, description }) => console.log('notification:', m, description),
  success: ({ message: m }) => console.log('notification success:', m),
};

// Dropdown
export const Dropdown = ({ menu, children }) => (
  <Popover content={menu?.items ? <Menu>{menu.items.map((i) => <MenuItem key={i.key} onClick={i.onClick}>{i.label}</MenuItem>)}</Menu> : menu}>
    {children}
  </Popover>
);

// Form implementation with contextual values and useForm API
const FormContext = React.createContext(null);
const FormConfigContext = React.createContext({
  layout: 'vertical',
  colon: true,
  labelAlign: 'left',
  labelWrap: false,
  labelCol: undefined,
  wrapperCol: undefined,
  requiredMark: true,
  disabled: false,
  size: undefined,
  variant: 'outlined',
  feedbackIcons: undefined,
  validateMessages: undefined,
  preserve: true,
});
const FormItemStatusContext = React.createContext({ status: undefined, errors: [], warnings: [] });

const defaultFeedbackIcons = {
  success: '✓',
  warning: '!',
  error: '⚠️',
  validating: '…',
};

const toNameKey = (name) => {
  if (Array.isArray(name)) return name.join('.');
  return name != null ? String(name) : '';
};

const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

const applyMessageTemplate = (template, variables = {}) => {
  if (!template) return undefined;
  return template.replace(/\$\{(\w+)}|\$\{\s*(\w+)\s*}/g, (_, key1, key2) => {
    const key = key1 || key2;
    return variables[key] ?? '';
  });
};

function createFormStore(initialValues = {}) {
  let values = { ...initialValues };
  let initial = { ...initialValues };
  let errors = {};
  let touched = {};
  const fieldListeners = new Map();
  const subscribers = new Set();
  const meta = new Map();
  const callbacks = {
    onValuesChange: null,
    onFieldsChange: null,
    onFinish: null,
    onFinishFailed: null,
  };

  const notifyField = (nameKey) => {
    const listeners = fieldListeners.get(nameKey);
    if (listeners) listeners.forEach((fn) => fn());
  };

  const notifySubscribers = (nameKey, changedValues) => {
    const snapshot = { ...values };
    subscribers.forEach((fn) => fn({ name: nameKey, allValues: snapshot, changedValues }));
    if (callbacks.onValuesChange && changedValues && Object.keys(changedValues).length) {
      callbacks.onValuesChange(changedValues, snapshot);
    }
    if (callbacks.onFieldsChange && changedValues && Object.keys(changedValues).length) {
      const allFields = Array.from(meta.keys()).map((key) => ({
        name: [key],
        value: values[key],
        errors: errors[key] || [],
      }));
      const changedFields = Object.keys(changedValues).map((key) => ({
        name: [key],
        value: changedValues[key],
        errors: errors[key] || [],
      }));
      callbacks.onFieldsChange(changedFields, allFields);
    }
  };

  const setFieldError = (nameKey, fieldErrors) => {
    if (fieldErrors.length) {
      errors = { ...errors, [nameKey]: fieldErrors };
    } else if (errors[nameKey]) {
      const next = { ...errors };
      delete next[nameKey];
      errors = next;
    }
    notifyField(nameKey);
  };

  const runRule = async (rule, value, allValues) => {
    if (!rule) return;
    const { message, validator, pattern, min, max, required, whitespace } = rule;
    if (validator) {
      await validator(rule, value, allValues);
      return;
    }
    if (required) {
      if (isEmptyValue(value) || (whitespace && typeof value === 'string' && !value.trim())) {
        throw new Error(message || 'This field is required');
      }
    }
    if (pattern && value != null && value !== '') {
      if (!pattern.test(value)) throw new Error(message || 'Value does not match pattern');
    }
    if (typeof value === 'string') {
      if (min != null && value.length < min) throw new Error(message || `Minimum ${min} characters`);
      if (max != null && value.length > max) throw new Error(message || `Maximum ${max} characters`);
    }
  };

  const validateField = async (nameKey, { trigger } = {}) => {
    const fieldMeta = meta.get(nameKey);
    if (!fieldMeta) return [];
    const value = values[nameKey];
    const allValues = { ...values };
    const rules = fieldMeta.rules || [];
    const effectiveRules = [...rules];
    const isRequired = fieldMeta.required || rules.some((rule) => rule && rule.required);
    if (isRequired && !rules.some((rule) => rule && rule.required)) {
      effectiveRules.unshift({ required: true, message: fieldMeta.requiredMessage });
    }
    const filteredRules = trigger
      ? effectiveRules.filter((rule) => {
          if (!rule || !rule.validateTrigger) return true;
          const triggers = Array.isArray(rule.validateTrigger) ? rule.validateTrigger : [rule.validateTrigger];
          return triggers.includes(trigger);
        })
      : effectiveRules;
    const collectedErrors = [];
    for (const rule of filteredRules) {
      if (!rule) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        await runRule(rule, value, allValues);
      } catch (err) {
        collectedErrors.push(err?.message || rule.message || 'Validation error');
        if (!rule?.validateFirst) break;
      }
    }
    setFieldError(nameKey, collectedErrors);
    return collectedErrors;
  };

  const validateFields = async (nameList, options = {}) => {
    const keys = nameList
      ? Array.isArray(nameList)
        ? nameList.map((name) => toNameKey(name))
        : [toNameKey(nameList)]
      : Array.from(meta.keys());
    const errorCollection = [];
    for (const key of keys) {
      // eslint-disable-next-line no-await-in-loop
      const fieldErrors = await validateField(key, { trigger: options.trigger });
      if (fieldErrors.length) {
        errorCollection.push({ name: [key], errors: fieldErrors });
      }
    }
    if (errorCollection.length) {
      const error = {
        values: { ...values },
        errorFields: errorCollection,
        outOfDate: false,
      };
      throw error;
    }
    return { ...values };
  };

  const setFieldValue = (name, value, { triggerValidation = true, trigger = 'change', skipTouch = false } = {}) => {
    const nameKey = toNameKey(name);
    const prev = values[nameKey];
    if (value === prev) {
      if (triggerValidation) validateField(nameKey, { trigger }).catch(() => null);
      return;
    }
    if (value === undefined) {
      const next = { ...values };
      delete next[nameKey];
      values = next;
    } else {
      values = { ...values, [nameKey]: value };
    }
    if (!skipTouch) touched[nameKey] = true;
    notifyField(nameKey);
    notifySubscribers(nameKey, { [nameKey]: value });
    if (triggerValidation) {
      validateField(nameKey, { trigger }).catch(() => null);
      Array.from(meta.entries())
        .filter(([, config]) => config?.dependencies?.some((dep) => dep === nameKey))
        .forEach(([depName]) => {
          validateField(depName, { trigger }).catch(() => null);
          notifyField(depName);
        });
    }
  };

  const setFieldsValue = (next, options = {}) => {
    if (!next || typeof next !== 'object') return;
    let changed = {};
    Object.keys(next).forEach((key) => {
      const nameKey = toNameKey(key);
      const value = next[key];
      if (value === undefined) {
        const cloned = { ...values };
        delete cloned[nameKey];
        values = cloned;
      } else {
        values = { ...values, [nameKey]: value };
      }
      changed = { ...changed, [nameKey]: value };
      touched = options.skipTouch ? touched : { ...touched, [nameKey]: true };
      notifyField(nameKey);
    });
    if (Object.keys(changed).length) {
      notifySubscribers(null, changed);
      if (options.triggerValidation !== false) {
        Object.keys(changed).forEach((key) => {
          validateField(key).catch(() => null);
        });
      }
    }
  };

  const registerField = (name, metaOptions = {}) => {
    const nameKey = toNameKey(name);
    const existing = meta.get(nameKey) || {};
    const merged = {
      ...existing,
      ...metaOptions,
      dependencies: (metaOptions.dependencies || []).map((dep) => toNameKey(dep)),
    };
    meta.set(nameKey, merged);
    if (metaOptions.initialValue !== undefined && values[nameKey] === undefined) {
      values = { ...values, [nameKey]: metaOptions.initialValue };
    }
    if (metaOptions.initialValue !== undefined) {
      initial = { ...initial, [nameKey]: metaOptions.initialValue };
    }
    return () => {
      meta.delete(nameKey);
      delete errors[nameKey];
    };
  };

  const subscribeField = (name, listener) => {
    const nameKey = toNameKey(name);
    if (!fieldListeners.has(nameKey)) fieldListeners.set(nameKey, new Set());
    const set = fieldListeners.get(nameKey);
    set.add(listener);
    return () => {
      set.delete(listener);
      if (!set.size) fieldListeners.delete(nameKey);
    };
  };

  const subscribe = (listener) => {
    subscribers.add(listener);
    return () => subscribers.delete(listener);
  };

  const resetFields = (names) => {
    if (!names) {
      values = { ...initial };
      errors = {};
      touched = {};
      Array.from(meta.keys()).forEach((key) => notifyField(key));
      notifySubscribers(null, { ...values });
      return;
    }
    const list = Array.isArray(names) ? names : [names];
    const changed = {};
    list.forEach((name) => {
      const key = toNameKey(name);
      if (initial[key] === undefined) {
        const next = { ...values };
        delete next[key];
        values = next;
      } else {
        values = { ...values, [key]: initial[key] };
      }
      delete errors[key];
      delete touched[key];
      changed[key] = values[key];
      notifyField(key);
    });
    notifySubscribers(null, changed);
  };

  const getFieldValue = (name) => values[toNameKey(name)];
  const getFieldsValue = (nameList) => {
    if (!nameList || nameList === true) return { ...values };
    const list = Array.isArray(nameList) ? nameList : [nameList];
    const result = {};
    list.forEach((name) => {
      const key = toNameKey(name);
      result[key] = values[key];
    });
    return result;
  };

  const getFieldError = (name) => errors[toNameKey(name)] || [];
  const getFieldsError = (nameList) => {
    if (!nameList) {
      return Object.keys(errors).map((key) => ({ name: [key], errors: errors[key] }));
    }
    const list = Array.isArray(nameList) ? nameList : [nameList];
    return list.map((name) => {
      const key = toNameKey(name);
      return { name: [key], errors: errors[key] || [] };
    });
  };

  const isFieldTouched = (name) => !!touched[toNameKey(name)];
  const isFieldsTouched = (nameList, allTouched = false) => {
    if (!nameList) {
      const touchedList = Object.values(touched);
      return allTouched ? touchedList.every(Boolean) : touchedList.some(Boolean);
    }
    const list = Array.isArray(nameList) ? nameList : [nameList];
    return allTouched
      ? list.every((name) => touched[toNameKey(name)])
      : list.some((name) => touched[toNameKey(name)]);
  };

  const setCallbacks = (next = {}) => {
    callbacks.onValuesChange = next.onValuesChange ?? callbacks.onValuesChange;
    callbacks.onFieldsChange = next.onFieldsChange ?? callbacks.onFieldsChange;
    callbacks.onFinish = next.onFinish ?? callbacks.onFinish;
    callbacks.onFinishFailed = next.onFinishFailed ?? callbacks.onFinishFailed;
  };

  const setInitialValues = (nextInitial, overwrite = false) => {
    if (!nextInitial) return;
    initial = overwrite ? { ...nextInitial } : { ...nextInitial, ...initial };
    if (overwrite) {
      values = { ...initial };
      errors = {};
      touched = {};
      Array.from(meta.keys()).forEach((key) => notifyField(key));
      notifySubscribers(null, { ...values });
    }
  };

  const submit = async () => {
    try {
      const result = await validateFields();
      callbacks.onFinish?.(result);
      return result;
    } catch (err) {
      callbacks.onFinishFailed?.(err);
      throw err;
    }
  };

  return {
    registerField,
    setFieldValue,
    setFieldsValue,
    setCallbacks,
    subscribeField,
    subscribe,
    getFieldValue,
    getFieldsValue,
    getFieldError,
    getFieldsError,
    validateField,
    validateFields,
    resetFields,
    isFieldTouched,
    isFieldsTouched,
    setInitialValues,
    submit,
  };
}

export const Form = ({
  children,
  form,
  className,
  style,
  layout = 'vertical',
  colon = true,
  labelAlign = 'left',
  labelWrap = false,
  labelCol,
  wrapperCol,
  requiredMark = true,
  disabled = false,
  size,
  variant = 'outlined',
  feedbackIcons,
  validateMessages,
  component = 'form',
  initialValues,
  onFinish,
  onFinishFailed,
  onFieldsChange,
  onValuesChange,
  scrollToFirstError,
  name,
  preserve = true,
  ...rest
}) => {
  const externalFormInstance = form;
  const internalInstanceRef = useRef();
  if (!externalFormInstance && !internalInstanceRef.current) {
    internalInstanceRef.current = createFormStore(initialValues || {});
  }
  const formInstance = externalFormInstance || internalInstanceRef.current;

  const appliedInitialRef = useRef(false);
  useEffect(() => {
    if (initialValues && !appliedInitialRef.current) {
      formInstance.setInitialValues(initialValues, true);
      formInstance.setFieldsValue(initialValues, { triggerValidation: false, skipTouch: true });
      appliedInitialRef.current = true;
    }
  }, [formInstance, initialValues]);

  useEffect(() => {
    formInstance.setCallbacks({ onFinish, onFinishFailed, onValuesChange, onFieldsChange });
  }, [formInstance, onFinish, onFinishFailed, onValuesChange, onFieldsChange]);

  const configValue = useMemo(
    () => ({
      layout,
      colon,
      labelAlign,
      labelWrap,
      labelCol,
      wrapperCol,
      requiredMark,
      disabled,
      size,
      variant,
      feedbackIcons,
      validateMessages,
      preserve,
    }),
    [layout, colon, labelAlign, labelWrap, labelCol, wrapperCol, requiredMark, disabled, size, variant, feedbackIcons, validateMessages, preserve],
  );

  const { onSubmit: customOnSubmit, ...restProps } = rest;

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    formInstance.submit().catch((err) => {
      if (scrollToFirstError && err?.errorFields?.length) {
        const [first] = err.errorFields;
        const fieldName = first?.name?.[0];
        if (typeof window !== 'undefined' && fieldName) {
          const el = document.querySelector(`[data-form-field="${fieldName}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (scrollToFirstError === true || scrollToFirstError?.focus) {
              const focusable = el.querySelector?.('input, select, textarea, button');
              focusable?.focus?.();
            }
          }
        }
      }
    });
    if (typeof customOnSubmit === 'function') {
      customOnSubmit(event);
    }
  };

  const WrapperComponent = component === false ? React.Fragment : component || 'form';
  const wrapperProps = component === false
    ? {}
    : {
        className,
        style,
        name,
        onSubmit: handleSubmit,
        ...restProps,
      };

  return (
    <FormContext.Provider value={formInstance}>
      <FormConfigContext.Provider value={configValue}>
        <WrapperComponent {...wrapperProps}>{children}</WrapperComponent>
      </FormConfigContext.Provider>
    </FormContext.Provider>
  );
};

const buildColStyle = (col) => {
  if (!col) return undefined;
  const span = col.span ?? col.flex;
  if (span != null) {
    const width = typeof span === 'number' ? `${(span / 24) * 100}%` : span;
    return { flex: `0 0 ${width}`, maxWidth: width };
  }
  return undefined;
};

const DSFormItem = ({
  children,
  className,
  style,
  label,
  name,
  initialValue,
  rules,
  required,
  help,
  extra,
  tooltip,
  colon,
  labelAlign,
  labelCol,
  wrapperCol,
  labelWrap,
  valuePropName = 'value',
  trigger = 'onChange',
  validateTrigger = 'onChange',
  getValueFromEvent,
  normalize,
  hasFeedback,
  feedbackIcons,
  validateStatus,
  hidden,
  noStyle,
  shouldUpdate,
  dependencies,
  htmlFor,
  requiredMark,
  messageVariables,
  preserve,
}) => {
  const form = useContext(FormContext) || createFormStore();
  const config = useContext(FormConfigContext);
  const [, forceRender] = useState(0);
  const nameKey = name != null ? toNameKey(name) : null;
  const mergedValidateTrigger = Array.isArray(validateTrigger) ? validateTrigger : [validateTrigger].filter(Boolean);
  const mergedTrigger = trigger || 'onChange';
  const lastShouldUpdateValues = useRef(form.getFieldsValue());
  const shouldPreserve = preserve ?? config?.preserve;

  useEffect(() => {
    if (nameKey == null) return undefined;
    const unregister = form.registerField(name, {
      rules,
      required,
      initialValue,
      dependencies,
      validateTrigger: mergedValidateTrigger,
      requiredMessage: applyMessageTemplate(
        // eslint-disable-next-line no-template-curly-in-string
        config?.validateMessages?.required || "'${label}' is required",
        {
          label: typeof label === 'string' ? label : toNameKey(name),
          name: toNameKey(name),
          ...messageVariables,
        },
      ),
    });
    if (initialValue !== undefined && form.getFieldValue(name) === undefined) {
      form.setFieldValue(name, initialValue, { triggerValidation: false, skipTouch: true });
    }
    return () => {
      unregister();
      if (!shouldPreserve && nameKey != null) {
        form.setFieldValue(name, undefined, { triggerValidation: false, skipTouch: true });
      }
    };
  }, [form, name, nameKey, rules, required, initialValue, mergedValidateTrigger, dependencies, config?.validateMessages, label, messageVariables, shouldPreserve]);

  useEffect(() => {
    if (nameKey == null) return undefined;
    return form.subscribeField(name, () => {
      forceRender((v) => v + 1);
    });
  }, [form, name, nameKey]);

  useEffect(() => {
    if (!shouldUpdate) return undefined;
    return form.subscribe(({ allValues }) => {
      if (typeof shouldUpdate === 'function') {
        const should = shouldUpdate(lastShouldUpdateValues.current, allValues);
        if (should) {
          lastShouldUpdateValues.current = allValues;
          forceRender((v) => v + 1);
        }
      } else {
        lastShouldUpdateValues.current = allValues;
        forceRender((v) => v + 1);
      }
    });
  }, [form, shouldUpdate]);

  const fieldValue = nameKey != null ? form.getFieldValue(name) : undefined;
  const fieldErrors = nameKey != null ? form.getFieldError(name) : [];

  const mergedLabelAlign = labelAlign || config?.labelAlign || 'left';
  const mergedLabelWrap = labelWrap ?? config?.labelWrap;
  const mergedLabelCol = labelCol || config?.labelCol;
  const mergedWrapperCol = wrapperCol || config?.wrapperCol;
  const mergedRequiredMark = requiredMark ?? config?.requiredMark;
  const mergedFeedbackIcons = feedbackIcons || config?.feedbackIcons;

  const isRequired = required || rules?.some((rule) => rule && rule.required);
  const mergedHelp = help !== undefined ? help : fieldErrors[0];
  const mergedStatus = validateStatus || (fieldErrors.length ? 'error' : undefined);
  const showFeedback = hasFeedback || mergedFeedbackIcons;

  const childNode = (() => {
    if (typeof children === 'function') {
      return children({
        getFieldValue: form.getFieldValue,
        getFieldsValue: form.getFieldsValue,
        getFieldError: form.getFieldError,
        getFieldsError: form.getFieldsError,
        values: form.getFieldsValue(),
      });
    }

    if (!React.isValidElement(children) || nameKey == null) return children;

    const originalTrigger = children.props?.[mergedTrigger];
    const originalBlur = children.props?.onBlur;

    const controlProps = {
      [mergedTrigger]: (...args) => {
        if (originalTrigger) originalTrigger(...args);
        let nextValue;
        if (getValueFromEvent) {
          nextValue = getValueFromEvent(...args);
        } else if (valuePropName === 'checked') {
          nextValue = args[0];
        } else if (args[0] && args[0].target) {
          const target = args[0].target;
          if (valuePropName in target) nextValue = target[valuePropName];
          else if ('value' in target) nextValue = target.value;
          else nextValue = args[0];
        } else {
          nextValue = args[0];
        }
        if (normalize) {
          nextValue = normalize(nextValue, fieldValue, form.getFieldsValue());
        }
        const shouldValidate = mergedValidateTrigger.includes('onChange');
        form.setFieldValue(name, nextValue, {
          triggerValidation: shouldValidate,
          trigger: 'onChange',
        });
      },
      onBlur: (...args) => {
        if (originalBlur) originalBlur(...args);
        if (mergedValidateTrigger.includes('onBlur')) {
          form.validateField(toNameKey(name), { trigger: 'onBlur' }).catch(() => null);
        }
      },
    };

    let rawValue;
    if (fieldValue !== undefined) {
      rawValue = fieldValue;
    } else if (children.props?.[valuePropName] !== undefined) {
      rawValue = children.props[valuePropName];
    }

    if (valuePropName === 'checked') {
      controlProps[valuePropName] = Boolean(rawValue);
    } else if (valuePropName === 'value') {
      const isPlainInput = typeof children.type === 'string'
        || children.type?.displayName === 'DesignSystemInput'
        || children.type?.displayName === 'DesignSystemInputPassword'
        || children.type?.displayName === 'DesignSystemInputTextArea';

      if (rawValue === undefined) {
        if (isPlainInput) controlProps[valuePropName] = '';
      } else if (rawValue === null) {
        controlProps[valuePropName] = isPlainInput ? '' : null;
      } else {
        controlProps[valuePropName] = rawValue;
      }
    } else if (rawValue !== undefined) {
      controlProps[valuePropName] = rawValue;
    }

    if (fieldErrors.length) {
      controlProps['aria-invalid'] = true;
      if (valuePropName === 'value') {
        controlProps.className = clsx(children.props.className, 'ds-form-control--error');
      }
    } else if (mergedStatus === 'warning' && valuePropName === 'value') {
      controlProps.className = clsx(children.props.className, 'ds-form-control--warning');
    }

    if (config?.disabled && children.props?.disabled === undefined) {
      controlProps.disabled = true;
    }
    if (config?.size && children.props?.size === undefined) {
      controlProps.size = config.size;
    }

    return React.cloneElement(children, controlProps);
  })();

  if (noStyle) {
    return <>{childNode}</>;
  }

  const layout = config?.layout || 'vertical';
  const labelStyle = layout === 'horizontal'
    ? { ...buildColStyle(mergedLabelCol), marginBottom: 0 }
    : layout === 'inline'
      ? { marginRight: 12, marginBottom: 0 }
      : { marginBottom: 8 };
  const controlStyle = layout === 'horizontal'
    ? { flex: 1, ...buildColStyle(mergedWrapperCol) }
    : layout === 'inline'
      ? { display: 'inline-flex', alignItems: 'center' }
      : {};
  const containerStyle = layout === 'horizontal'
    ? { display: 'flex', width: '100%', gap: 12, alignItems: mergedLabelAlign === 'top' ? 'flex-start' : 'center' }
    : layout === 'inline'
      ? { display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }
      : { display: 'grid' };

  const colonChar = '';
  let requiredMarkNode = null;
  if (mergedRequiredMark === false) {
    requiredMarkNode = null;
  } else if (mergedRequiredMark === 'optional') {
    requiredMarkNode = null;
  } else if (isRequired) {
    requiredMarkNode = (
      <span style={{ color: 'var(--danger)', marginLeft: 4 }}>*</span>
    );
  }

  const feedbackIconNode = showFeedback ? (() => {
    const icons = mergedFeedbackIcons || defaultFeedbackIcons;
    const icon = icons[mergedStatus || 'success'];
    if (!icon) return null;
    return (
      <span
        style={{
          marginLeft: 6,
          color:
            mergedStatus === 'error'
              ? 'var(--danger)'
              : mergedStatus === 'warning'
                ? 'var(--warning)'
                : 'var(--brand)',
        }}
      >
        {icon}
      </span>
    );
  })() : null;

  const mergedHidden = hidden ? { display: 'none' } : undefined;

  const labelContent = label ? (
    <label
      htmlFor={htmlFor || undefined}
      style={{
        display: 'block',
        fontWeight: 600,
        textAlign: mergedLabelAlign,
        // whiteSpace: mergedLabelWrap ? 'normal' : 'nowrap',
        ...(layout === 'horizontal' ? { paddingTop: 6 } : {}),
        ...labelStyle,
      }}
      data-form-field-label={nameKey ?? undefined}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {colonChar}
        {requiredMarkNode}
        {tooltip ? <span style={{ color: 'var(--text-muted)' }}>{tooltip}</span> : null}
      </span>
    </label>
  ) : layout === 'horizontal' ? (
    <span style={{ ...labelStyle }} />
  ) : null;

  const shouldStretchControl = (config?.layout || 'vertical') !== 'inline';

  const controlRowStyle = feedbackIconNode
    ? { display: 'flex', alignItems: 'center', gap: 6, width: shouldStretchControl ? '100%' : undefined }
    : { width: shouldStretchControl ? '100%' : undefined };

  const controlRowContent = feedbackIconNode
    ? (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            {childNode}
          </div>
          {feedbackIconNode}
        </>
      )
    : childNode;

  const controlContent = (
    <div
      data-form-field={nameKey ?? undefined}
      style={{ display: 'flex', flexDirection: 'column', gap: 6, ...controlStyle }}
    >
      <div style={controlRowStyle}>
        {controlRowContent}
      </div>
      {mergedHelp ? (
        <div
          style={{
            fontSize: 12,
            color:
              mergedStatus === 'warning'
                ? 'var(--warning)'
                : fieldErrors.length
                  ? 'var(--danger)'
                  : 'var(--text-muted)',
          }}
        >
          {mergedHelp}
        </div>
      ) : null}
      {extra ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{extra}</div> : null}
    </div>
  );

  return (
    <FormItemStatusContext.Provider value={{ status: mergedStatus, errors: fieldErrors, warnings: [] }}>
      <div className={className} style={{ marginBottom: 16, ...style, ...mergedHidden }}>
        <div style={containerStyle}>
          {labelContent}
          {controlContent}
        </div>
      </div>
    </FormItemStatusContext.Provider>
  );
};

Form.Item = DSFormItem;

Form.useForm = () => {
  const formRef = useRef();
  if (!formRef.current) formRef.current = createFormStore();
  return [formRef.current];
};

Form.useFormInstance = () => {
  const instance = useContext(FormContext);
  if (!instance) throw new Error('Form.useFormInstance must be used within a Form');
  return instance;
};

Form.useWatch = (namePath, formInstanceOrOptions) => {
  const contextForm = useContext(FormContext);
  let options = {};
  let instance = formInstanceOrOptions;
  if (formInstanceOrOptions && typeof formInstanceOrOptions === 'object' && !('setFieldValue' in formInstanceOrOptions)) {
    options = formInstanceOrOptions || {};
    instance = options.form;
  }
  const form = instance || contextForm || createFormStore();
  const selector = typeof namePath === 'function' ? namePath : null;

  const getSnapshot = () => {
    if (selector) return selector(form.getFieldsValue());
    if (namePath === true || namePath == null) return form.getFieldsValue(namePath);
    return form.getFieldValue(namePath);
  };

  const [value, setValue] = useState(getSnapshot);

  useEffect(() => {
    return form.subscribe(({ allValues, name }) => {
      if (selector) {
        setValue(selector(allValues));
        return;
      }
      if (namePath === true || namePath == null) {
        setValue(form.getFieldsValue(namePath));
        return;
      }
      const key = toNameKey(namePath);
      if (name == null || name === key) {
        setValue(form.getFieldValue(namePath));
      }
    });
  }, [form, selector, namePath]);

  return value;
};

const useFormItemStatus = () => useContext(FormItemStatusContext);

Form.Item.useStatus = useFormItemStatus;

// styling helpers
export const makeStyles = (styles) => () => (typeof styles === 'function' ? styles() : styles);
export const withStyles = (styles) => (Comp) => (props) => <Comp {...props} classes={typeof styles === 'function' ? styles() : styles} />;
export const styled = (Comp) => (styleFn) => (props) => <Comp {...props} style={{ ...(props.style||{}), ...(typeof styleFn === 'function' ? styleFn(props) : styleFn) }} />;
export const useTheme = () => ({});

// Misc material compat
export const Fade = ({ children }) => <>{children}</>;
export const Popper = Popover;
export const FormControl = ({ children, className, style }) => <div className={className} style={style}>{children}</div>;
export const InputLabel = ({ children, className, style }) => <label className={className} style={style}>{children}</label>;
export const FilledInput = (props) => <Input {...props} />;
export const Breadcrumbs = ({ children }) => <nav>{children}</nav>;
export const Dialog = Modal;
export const SwipeableDrawer = Drawer;
export const FormControlLabel = ({ control, label }) => <label style={{ display:'inline-flex', alignItems:'center', gap:8 }}>{control}{label}</label>;

// MUI X compat (no-op)
export const LocalizationProvider = ({ children }) => <>{children}</>;
export const AdapterDayjs = {};
export const renderTimeViewClock = () => null;
